import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaInbox, FaFilter, FaMapMarkedAlt } from "react-icons/fa";
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { API } from 'aws-amplify';
import '../../../styles/compact-filters.css';
import '../../../components/PageHeader/PageHeader.css';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useI18n } from '../../../i18n';
import './AssetLocator.css';
import Map from '../../../components/Map/Map';
import { getMapView } from '../../../theme/palette';
import { useDisplayMode } from '../../../theme/displayMode';

import { ASSET_TYPE } from '../../../localdb';

const MAP_OPTIONS = {
    center: { lat: 53.65, lng: -6.68 },
    zoom: 10,
    scrollwheel: true,
    mapId: '4ada5c0171a1e340',
    mapTypeControl: true,
    fullscreenControl: true,
    zoomControl: true,
    streetViewControl: false,
};

const FILTERS_KEY = 'ipsum.assetLocator.filters';

function loadSavedFilters() {
    try {
        const saved = JSON.parse(sessionStorage.getItem(FILTERS_KEY) || '{}');
        return saved && typeof saved === 'object' ? saved : {};
    } catch (error) {
        return {};
    }
}

const TYPE_COLORS = {
    'Fibre Optic': '#D7B84A',
    'Pedestrian Crossing': '#5B8FCF',
    'Pelican Crossing': '#5FA56B',
    'School Warning Sign': '#5FB7C4',
    'Signalised Junction': '#D46A7A',
    'Toucan Crossing': '#C75B5B',
    'Zebra Crossing': '#8A73B8',
    'Sign - Other': '#D48A3C',
    'CCTV': '#4E9A7A',
    'Design & Construction': '#6AA3C8',
    'Periodic Speed Limit Sign': '#8FB84F',
    'Public Lighting': '#B8874A',
    'Other': '#8A97A8',
};

const apiHeaders = {
    'x-api-key': process.env.REACT_APP_API_KEY,
    'Accept': 'application/json',
    'Content-Type': 'application/json',
};

const locatorLayer = {
    map: null,
    links: [],
    markers: [],
    listener: null,
    infoWindow: null,
    onSelect: null,
    selectedId: null,
};

function hasCoords(link) {
    return link && link.coords && Number.isFinite(link.coords.lat) && Number.isFinite(link.coords.lng)
        && !(link.coords.lat === 0 && link.coords.lng === 0);
}

function colorForType(type) {
    return TYPE_COLORS[type] || TYPE_COLORS.Other;
}

function matchesType(item, type) {
    if (!type) return true;
    const value = (item.type || '').toLowerCase();
    if (type === 'All Signs') {
        return value.includes('school warning sign')
            || value.includes('sign - other')
            || value.includes('periodic speed limit sign');
    }
    return value.includes(type.toLowerCase());
}

function hasAssetCoords(item) {
    const lat = item && item.latitude ? parseFloat(item.latitude) : 0;
    const lng = item && item.longitude ? parseFloat(item.longitude) : 0;
    return Boolean(lat || lng);
}

function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function markerInfoHtml(asset) {
    const ref = escapeHtml(asset.site_ref || 'Asset');
    const address = escapeHtml(asset.site_address || 'No address');
    const district = escapeHtml(asset.district || '');
    const type = escapeHtml(asset.type || 'Other');
    const color = colorForType(asset.type);
    const priority = asset.hp_site === true ? 'High' : 'Low';
    const details = [district, type].filter(Boolean).join(' · ');
    return (
        '<div class="locator-iw">' +
            '<div class="locator-iw-head">' +
                '<span class="locator-iw-dot" style="background:' + color + '"></span>' +
                '<strong>' + ref + '</strong>' +
            '</div>' +
            '<p class="locator-iw-address">' + address + '</p>' +
            (details ? '<p class="locator-iw-meta">' + details + '</p>' : '') +
            '<div class="locator-iw-foot">' +
                '<span class="locator-iw-priority' + (priority === 'High' ? ' is-high' : '') + '">' + priority + ' priority</span>' +
                '<a class="locator-iw-open" href="/asset_det/' + encodeURIComponent(asset.assetId) + '">Open</a>' +
            '</div>' +
        '</div>'
    );
}

function clearLayer() {
    locatorLayer.markers.forEach((item) => {
        if (item.marker) item.marker.map = null;
    });
    locatorLayer.markers = [];
    if (locatorLayer.infoWindow) {
        locatorLayer.infoWindow.close();
        locatorLayer.infoWindow = null;
    }
}

function clusterSize(zoom) {
    if (zoom >= 14) return 0;
    if (zoom >= 12) return 0.012;
    if (zoom >= 10) return 0.035;
    return 0.09;
}

function clusterGroups(links, zoom) {
    const cell = clusterSize(zoom);
    const mappable = links.filter(hasCoords);
    if (!cell) return mappable.map((link) => ({ type: 'pin', links: [link] }));
    const buckets = {};
    mappable.forEach((link) => {
        const key = `${Math.round(link.coords.lat / cell)}_${Math.round(link.coords.lng / cell)}`;
        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(link);
    });
    return Object.keys(buckets).map((key) => ({
        type: buckets[key].length === 1 ? 'pin' : 'cluster',
        links: buckets[key],
    }));
}

function pinNode(color, selected) {
    const wrap = document.createElement('div');
    wrap.className = selected ? 'locator-pin is-selected' : 'locator-pin';
    wrap.style.setProperty('--pin-color', color || TYPE_COLORS.Other);
    return wrap;
}

function applyMapControls(map) {
    if (!window.google || !window.google.maps) return;
    const pos = window.google.maps.ControlPosition;
    map.setOptions({
        mapTypeControl: true,
        mapTypeControlOptions: {
            style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: pos.TOP_LEFT,
        },
        fullscreenControl: true,
        fullscreenControlOptions: { position: pos.RIGHT_TOP },
        zoomControl: true,
        zoomControlOptions: { position: pos.RIGHT_BOTTOM },
        streetViewControl: false,
    });
}

function clusterNode(count) {
    const wrap = document.createElement('div');
    wrap.className = 'locator-cluster';
    wrap.textContent = count > 99 ? '99+' : String(count);
    return wrap;
}

function openInfo(map, marker, content) {
    if (locatorLayer.infoWindow) locatorLayer.infoWindow.close();
    locatorLayer.infoWindow = new window.google.maps.InfoWindow({
        content,
        maxWidth: 280,
        pixelOffset: new window.google.maps.Size(0, -4),
    });
    locatorLayer.infoWindow.open(map, marker);
}

function renderLayer() {
    if (!locatorLayer.map || !window.google || !window.google.maps.marker) return;
    clearLayer();
    const map = locatorLayer.map;
    const groups = clusterGroups(locatorLayer.links, map.getZoom() || 10);
    groups.forEach((group) => {
        if (group.type === 'cluster') {
            const lat = group.links.reduce((sum, item) => sum + item.coords.lat, 0) / group.links.length;
            const lng = group.links.reduce((sum, item) => sum + item.coords.lng, 0) / group.links.length;
            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                map,
                position: { lat, lng },
                title: `${group.links.length} assets`,
                content: clusterNode(group.links.length),
            });
            marker.addListener('click', () => {
                map.panTo({ lat, lng });
                map.setZoom(Math.min(18, (map.getZoom() || 10) + 2));
            });
            locatorLayer.markers.push({ id: null, marker });
            return;
        }
        const link = group.links[0];
        const contentEl = pinNode(link.color, locatorLayer.selectedId === link.id);
        const marker = new window.google.maps.marker.AdvancedMarkerElement({
            map,
            position: link.coords,
            title: link.title,
            content: contentEl,
        });
        marker.addListener('click', () => {
            locatorLayer.selectedId = link.id;
            locatorLayer.markers.forEach((item) => {
                if (item.el) item.el.classList.toggle('is-selected', item.id === link.id);
            });
            openInfo(map, marker, link.content);
            if (locatorLayer.onSelect) locatorLayer.onSelect(link.id);
        });
        locatorLayer.markers.push({ id: link.id, marker, el: contentEl, link });
    });
}

async function addMarkers(map, links) {
    locatorLayer.map = map;
    locatorLayer.links = links || [];
    if (locatorLayer.listener) {
        window.google.maps.event.removeListener(locatorLayer.listener);
        locatorLayer.listener = null;
    }
    await window.google.maps.importLibrary('marker');
    applyMapControls(map);
    renderLayer();
    locatorLayer.listener = map.addListener('zoom_changed', renderLayer);
    return () => {
        if (locatorLayer.listener) {
            window.google.maps.event.removeListener(locatorLayer.listener);
            locatorLayer.listener = null;
        }
        clearLayer();
    };
}

function focusAsset(id) {
    const map = locatorLayer.map;
    const link = (locatorLayer.links || []).find((item) => item.id === id);
    if (!map || !link || !hasCoords(link)) return;
    locatorLayer.selectedId = id;
    map.panTo(link.coords);
    if ((map.getZoom() || 10) < 15) map.setZoom(15);
    const open = () => {
        renderLayer();
        const entry = locatorLayer.markers.find((item) => item.id === id);
        if (entry && entry.marker) openInfo(map, entry.marker, link.content);
    };
    window.google.maps.event.addListenerOnce(map, 'idle', open);
}

const AssetLocator = () => {
    const { t, tCatalog } = useI18n();
    const { theme } = useDisplayMode();
    const mapView = getMapView();
    const saved = loadSavedFilters();
    const [assets, setAssets] = useState([]);
    const [assetList, setAssetList] = useState([]);
    const [district, setDistrict] = useState(saved.district || '');
    const [siteRef, setSiteRef] = useState(saved.siteRef || '');
    const [siteAddress, setSiteAddress] = useState(saved.siteAddress || '');
    const [assetType, setAssetType] = useState(saved.assetType || '');
    const [missingLocation, setMissingLocation] = useState(Boolean(saved.missingLocation));
    const [highPriority, setHighPriority] = useState(Boolean(saved.highPriority));
    const [sortBy, setSortBy] = useState(saved.sortBy || 'site-az');
    const [moreOpen, setMoreOpen] = useState(Boolean(saved.missingLocation || saved.highPriority));
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [mapTasks, setMapTasks] = useState([]);
    const [districtList, setDistrictList] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [listCollapsed, setListCollapsed] = useState(false);
    const [listWidth, setListWidth] = useState(() => Math.min(380, Math.max(260, Math.round((typeof window !== 'undefined' ? window.innerWidth : 1200) * 0.3))));
    const [legendOpen, setLegendOpen] = useState(false);
    const [legendOverflow, setLegendOverflow] = useState(false);
    const selectedIdRef = useRef(null);
    const listRef = useRef(null);
    const legendRef = useRef(null);
    selectedIdRef.current = selectedId;
    locatorLayer.onSelect = setSelectedId;

    const params = { headers: apiHeaders };

    useEffect(() => {
        const fetchAssets = async () => {
            setIsLoading(true);
            setLoadError('');
            try {
                const next = await API.get('asset_locator', '/', params) || [];
                next.forEach((asset) => {
                    asset.hp_site_str = asset.hp_site === true ? 'High' : 'Low';
                    if (!asset.type) asset.type = '';
                    if (!asset.district) asset.district = '';
                });
                next.sort((a, b) => (a.site_ref > b.site_ref) ? 1 : -1);
                setAssets(next);
                setAssetList(next);
            } catch (error) {
                console.log(error);
                setLoadError('Could not load assets.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchAssets();
    }, []);

    useEffect(() => {
        const fetchDistrict = async () => {
            const districts = await API.get('settings', '/DISTRICTS', params);
            const unique = [...new Set((districts.values || []).filter(Boolean))];
            setDistrictList(unique.map((name) => (
                <option key={name} value={name}>{name}</option>
            )));
        };
        fetchDistrict();
    }, []);

    useEffect(() => {
        const links = assetList.map((asset) => {
            const lat = asset.latitude ? parseFloat(asset.latitude) : 0;
            const long = asset.longitude ? parseFloat(asset.longitude) : 0;
            return {
                id: asset.assetId,
                title: asset.site_ref,
                coords: { lat, lng: long },
                color: colorForType(asset.type),
                content: markerInfoHtml(asset),
            };
        });
        setMapTasks(links.filter(hasCoords));
    }, [assetList]);

    const handleSearch = useCallback(() => {
        let results = assets;
        if (siteRef) {
            const query = siteRef.toLowerCase();
            results = results.filter((item) => (
                (item.site_ref || '').toLowerCase().includes(query)
                || (item.site_address || '').toLowerCase().includes(query)
                || (item.notes || '').toLowerCase().includes(query)
                || (item.fault_description || '').toLowerCase().includes(query)
            ));
        }
        if (siteAddress) {
            results = results.filter((item) =>
                (item.site_address || '').toLowerCase().includes(siteAddress.toLowerCase()));
        }
        if (district) {
            results = results.filter((item) =>
                (item.district || '').toLowerCase().includes(district.toLowerCase()));
        }
        if (assetType) results = results.filter((item) => matchesType(item, assetType));
        if (highPriority) results = results.filter((item) => item.hp_site === true);
        if (missingLocation) results = results.filter((item) => !hasAssetCoords(item));
        const sorted = results.slice().sort((a, b) => {
            if (sortBy === 'site-za') return String(b.site_ref || '').localeCompare(String(a.site_ref || ''));
            if (sortBy === 'type') return String(a.type || '').localeCompare(String(b.type || ''));
            return String(a.site_ref || '').localeCompare(String(b.site_ref || ''));
        });
        setAssetList(sorted);
    }, [assets, siteRef, siteAddress, district, assetType, highPriority, missingLocation, sortBy]);

    useEffect(() => {
        handleSearch();
    }, [handleSearch]);

    useEffect(() => {
        sessionStorage.setItem(FILTERS_KEY, JSON.stringify({
            siteRef, siteAddress, district, assetType, missingLocation, highPriority, sortBy,
        }));
    }, [siteRef, siteAddress, district, assetType, missingLocation, highPriority, sortBy]);

    const handleClearSearch = () => {
        setDistrict('');
        setSiteRef('');
        setSiteAddress('');
        setAssetType('');
        setMissingLocation(false);
        setHighPriority(false);
        setSelectedId(null);
        locatorLayer.selectedId = null;
    };

    const removeFilter = (key) => {
        if (key === 'siteRef') setSiteRef('');
        if (key === 'siteAddress') setSiteAddress('');
        if (key === 'district') setDistrict('');
        if (key === 'assetType') setAssetType('');
        if (key === 'missingLocation') setMissingLocation(false);
        if (key === 'highPriority') setHighPriority(false);
    };

    const handleSelLocation = (id) => {
        setSelectedId(id);
        locatorLayer.selectedId = id;
        focusAsset(id);
    };

    const handleLegendClick = (type) => {
        setAssetType((current) => (current === type ? '' : type));
    };

    const startResize = (event) => {
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = listWidth;
        const onMove = (moveEvent) => {
            setListWidth(Math.min(560, Math.max(240, startWidth + moveEvent.clientX - startX)));
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    useEffect(() => {
        if (!selectedId || !listRef.current) return;
        const row = listRef.current.querySelector(`[data-asset-id="${selectedId}"]`);
        if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
    }, [selectedId]);

    useEffect(() => {
        if (selectedId && !assetList.some((item) => item.assetId === selectedId)) {
            setSelectedId(null);
            locatorLayer.selectedId = null;
        }
    }, [assetList, selectedId]);

    useEffect(() => {
        const el = legendRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const check = () => setLegendOverflow(el.scrollHeight > el.clientHeight + 1);
        check();
        const observer = new ResizeObserver(check);
        observer.observe(el);
        return () => observer.disconnect();
    }, [legendOpen, assets, assetType]);

    const activeChips = [
        siteRef && { key: 'siteRef', label: `${t('fields.siteRef')}: ${siteRef}` },
        siteAddress && { key: 'siteAddress', label: `${t('common.address')}: ${siteAddress}` },
        district && { key: 'district', label: district },
        assetType && { key: 'assetType', label: tCatalog(assetType) },
        highPriority && { key: 'highPriority', label: t('locator.highPriority') },
        missingLocation && { key: 'missingLocation', label: t('locator.noMapLocation') },
    ].filter(Boolean);
    const filtersActive = activeChips.length > 0;
    const typeCounts = useMemo(() => {
        const counts = {};
        ASSET_TYPE.forEach((type) => { counts[type] = 0; });
        assets.forEach((item) => {
            const type = ASSET_TYPE.includes(item.type) ? item.type : 'Other';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [assets]);

    const mapOptions = useMemo(() => ({
        ...MAP_OPTIONS,
        center: mapView.center,
        zoom: mapView.zoom,
        colorScheme: theme === 'dark' ? 'DARK' : 'LIGHT',
    }), [theme, mapView.center.lat, mapView.center.lng, mapView.zoom]);

    const mapProps = useMemo(() => ({
        options: mapOptions,
        onMount: addMarkers,
        onMountProps: mapTasks,
        frameProps: mapTasks,
        fitToMarkers: true,
        className: 'gmaps',
    }), [mapTasks, mapOptions]);

    return (
        <div className="AssetLocator">
            <PageHeader
                title={t('pages.assetLocator.title')}
                breadcrumbs={[
                    { label: t('nav.assets'), path: '/assets' },
                    { label: t('pages.assetLocator.crumb') },
                ]}
                count={!isLoading ? assetList.length : undefined}
                countLabel={t('nav.assets')}
                subtitle={t('pages.assetLocator.subtitle')}
            />
            <div className="locator-body">
                <div className="locator-toolbar compact-filters">
                    <div className="locator-filter-row">
                        <span className="locator-kicker">
                            <FaFilter /> {t('locator.filters')}
                            {filtersActive && (
                                <span className="locator-filter-count">{activeChips.length}</span>
                            )}
                        </span>
                        <Form.Group controlId="site_ref" className="filter-field">
                            <Form.Label>{t('fields.siteRef')}</Form.Label>
                            <Form.Control
                                as="input"
                                value={siteRef}
                                placeholder={t('users.search')}
                                onChange={(e) => setSiteRef(e.target.value)}
                                type="text"
                            />
                        </Form.Group>
                        <Form.Group controlId="site_address" className="filter-field">
                            <Form.Label>{t('fields.siteAddress')}</Form.Label>
                            <Form.Control
                                as="input"
                                value={siteAddress}
                                placeholder={t('users.search')}
                                onChange={(e) => setSiteAddress(e.target.value)}
                                type="text"
                            />
                        </Form.Group>
                        <Form.Group controlId="district" className="filter-field">
                            <Form.Label>{t('common.district')}</Form.Label>
                            <Form.Control
                                as="select"
                                value={district}
                                onChange={(e) => setDistrict(e.target.value)}
                            >
                                <option value="">{t('locator.allDistricts')}</option>
                                {districtList}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group controlId="assetType" className="filter-field">
                            <Form.Label>{t('fields.assetType')}</Form.Label>
                            <Form.Control
                                as="select"
                                value={assetType}
                                onChange={(e) => setAssetType(e.target.value)}
                            >
                                <option value="">{t('locator.allTypes')}</option>
                                {ASSET_TYPE.map((type) => (
                                    <option key={type} value={type}>{tCatalog(type)}</option>
                                ))}
                                <option value="All Signs">{t('locator.allSigns')}</option>
                            </Form.Control>
                        </Form.Group>
                        <div className="locator-filter-actions">
                            <button
                                type="button"
                                className={`locator-more${moreOpen ? ' is-open' : ''}`}
                                onClick={() => setMoreOpen((open) => !open)}
                            >
                                {t('common.moreFilters')}
                            </button>
                            <button
                                type="button"
                                className="locator-clear"
                                onClick={handleClearSearch}
                                disabled={!filtersActive}
                            >
                                <FaTimes /> {t('actions.clear')}
                            </button>
                        </div>
                    </div>
                    {moreOpen && (
                        <div className="locator-more-row">
                            <label>
                                <input type="checkbox" checked={highPriority} onChange={(e) => setHighPriority(e.target.checked)} />
                                {t('locator.highPriority')}
                            </label>
                            <label>
                                <input type="checkbox" checked={missingLocation} onChange={(e) => setMissingLocation(e.target.checked)} />
                                {t('locator.noMapLocation')}
                            </label>
                        </div>
                    )}
                    {filtersActive && (
                        <div className="locator-chips">
                            {activeChips.map((chip) => (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className="locator-chip"
                                    onClick={() => removeFilter(chip.key)}
                                >
                                    {chip.label} <FaTimes />
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="locator-legend-row">
                        <span className="locator-kicker"><FaMapMarkedAlt /> {t('locator.mapLegend')}</span>
                        <div
                            ref={legendRef}
                            className={`locator-legend${legendOpen ? ' is-open' : ''}`}
                            role="group"
                            aria-label={t('locator.assetTypes')}
                        >
                            {ASSET_TYPE.map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={`legend-chip${assetType === type ? ' is-active' : ''}${assetType && assetType !== type ? ' is-dim' : ''}`}
                                    style={{ '--legend-color': colorForType(type) }}
                                    onClick={() => handleLegendClick(type)}
                                >
                                    <span className="legend-dot" />
                                    {tCatalog(type)}
                                    <em>{typeCounts[type] || 0}</em>
                                </button>
                            ))}
                        </div>
                        {(legendOverflow || legendOpen) && (
                            <button
                                type="button"
                                className="legend-more"
                                onClick={() => setLegendOpen((open) => !open)}
                            >
                                {legendOpen ? t('locator.showLess') : t('locator.showAll')}
                            </button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="loading-container">
                        <div className="loading-content">
                            <Spinner animation="border" role="status" className="loading-spinner" />
                            <div className="loading-text">{t('locator.loading')}</div>
                            <div className="loading-subtext">{t('locator.pleaseWait')}</div>
                        </div>
                    </div>
                ) : loadError ? (
                    <div className="empty-state locator-error">
                        <div className="empty-state-text">{loadError}</div>
                        <div className="empty-state-subtext">{t('locator.retryLater')}</div>
                    </div>
                ) : (
                    <div className="locator-workspace">
                        {!listCollapsed && (
                            <aside className="locator-list" style={{ width: listWidth }} ref={listRef}>
                                <div className="locator-list-head">
                                    <div>
                                        <strong>{t('nav.assets')}</strong>
                                        <span>{t('locator.results', { count: assetList.length })}</span>
                                    </div>
                                    <label className="locator-sort">
                                        {t('locator.sortBy')}
                                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                            <option value="site-az">{t('locator.siteAz')}</option>
                                            <option value="site-za">{t('locator.siteZa')}</option>
                                            <option value="type">{t('locator.sortType')}</option>
                                        </select>
                                    </label>
                                </div>
                                {assetList.length === 0 ? (
                                    <div className="empty-state">
                                        <FaInbox className="empty-state-icon" />
                                        <div className="empty-state-text">{t('locator.noRecords')}</div>
                                        <div className="empty-state-subtext">{t('locator.tryFilters')}</div>
                                    </div>
                                ) : (
                                    <div className="locator-list-items">
                                        {assetList.map((item) => (
                                            <div
                                                className={`locator-item${selectedId === item.assetId ? ' is-selected' : ''}${hasAssetCoords(item) ? '' : ' is-unmapped'}`}
                                                key={item.assetId}
                                                data-asset-id={item.assetId}
                                            >
                                                <button
                                                    type="button"
                                                    className="item-select"
                                                    onClick={() => handleSelLocation(item.assetId)}
                                                >
                                                    <span
                                                        className="item-dot"
                                                        style={{ '--legend-color': colorForType(item.type) }}
                                                    />
                                                    <span className="item-copy">
                                                        <span className="item-ref">{item.site_ref}</span>
                                                        <span className="item-meta">
                                                            <span className="item-address">{item.site_address || t('assets.noAddress')}</span>
                                                            <span className="item-type">{item.type ? tCatalog(item.type) : t('locator.other')}</span>
                                                        </span>
                                                    </span>
                                                </button>
                                                <a
                                                    className="item-open"
                                                    href={`/asset_det/${item.assetId}`}
                                                    aria-label={t('locator.openAsset', { ref: item.site_ref })}
                                                >
                                                    <FaChevronRight />
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </aside>
                        )}
                        {!listCollapsed && (
                            <div
                                className="locator-resizer"
                                onMouseDown={startResize}
                                role="separator"
                                aria-orientation="vertical"
                                aria-label={t('locator.resizeList')}
                            />
                        )}
                        <div className="locator-map">
                            <button
                                type="button"
                                className="locator-collapse"
                                onClick={() => setListCollapsed((open) => !open)}
                                aria-pressed={listCollapsed}
                                title={listCollapsed ? t('locator.showList') : t('locator.expandMap')}
                            >
                                {listCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                            </button>
                            <Map {...mapProps} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssetLocator;
